// src/components/enterprise/B2BEmbedPortal.tsx
import React, { useState, useEffect } from 'react';
import { Lock, ShieldCheck } from 'lucide-react';
import OmnioTemplate from './OmnioTemplate';

const B2B_API_BASE =
  window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? 'http://localhost:4001'
    : 'https://sesgelio.com';

// Şirket ve Partner adını temiz URL uzantısına çevirir (Örn: "Teknodes Yazılım A.Ş." -> "teknodes-yazilim-as")
const toUrlSlug = (text?: string) =>
  (text || 'kurumsal')
    .toLowerCase()
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ı/g, 'i')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

export const B2BEmbedPortal: React.FC = () => {
  const params = new URLSearchParams(window.location.search);

  // 🔒 URL'DE ASLA VKN VEYA API KEY TAŞINMAZ! Sadece şifreli 'session' (Token) veya 'guestToken' gelir.
  const urlSessionToken = params.get('session') || sessionStorage.getItem('b2b_embed_session');
  const guestToken = params.get('guestToken');

  const [loading, setLoading] = useState<boolean>(true);
  const [unauthorized, setUnauthorized] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [sessionData, setSessionData] = useState<any>(null);

  const [guestName, setGuestName] = useState<string>('');
  const [waitingGuestName, setWaitingGuestName] = useState<boolean>(Boolean(guestToken));

  useEffect(() => {
    // Dış müşteri davet linkiyse (guestToken) direkt isim sorma ekranını aç
    if (guestToken) {
      setLoading(false);
      return;
    }

    // Şifreli session token yoksa içeri ASLA alma
    if (!urlSessionToken) {
      setUnauthorized(true);
      setLoading(false);
      return;
    }

// 🛡️ ADRES ÇUBUĞUNU ANINDA TEMİZLE: Linkteki ?session=... silinsin, mevcut özel uzantı korunsun!
    sessionStorage.setItem('b2b_embed_session', urlSessionToken);
    window.history.replaceState({}, document.title, window.location.pathname);

    const verifyEncryptedSession = async () => {
      try {
        const res = await fetch(`${B2B_API_BASE}/api/b2b/session-verify`, {
          headers: { Authorization: `Bearer ${urlSessionToken}` }
        });

        const data = await res.json();
        if (!res.ok) {
          sessionStorage.removeItem('b2b_embed_session');
          setErrorMsg(data.error || 'Oturum anahtarı geçersiz veya süresi dolmuş.');
          setUnauthorized(true);
        } else {
          setSessionData(data);
          // Oturum doğrulandığında URL'yi otomatik olarak "/embed/omnio/teknodes-yazilim-as" formatına çevir
          const partnerSlug = toUrlSlug(data.theme?.brandTitle?.split(' ')[0] || 'partner');
          const companySlug = toUrlSlug(data.company?.name || 'firma');
          window.history.replaceState({}, data.company?.name || document.title, `/embed/${partnerSlug}/${companySlug}`);
        }
      } catch (err) {
        setErrorMsg('İletişim sunucusuna bağlanılamadı.');
      } finally {
        setLoading(false);
      }
    };

    verifyEncryptedSession();
  }, []);
  
  const handleGuestJoin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!guestName.trim() || !guestToken) return;
    setLoading(true);
    try {
      const res = await fetch(`${B2B_API_BASE}/api/b2b/guest-join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ guestToken, guestName: guestName.trim() })
      });
      const data = await res.json();
      if (!res.ok) {
        setErrorMsg(data.error || 'Davet linki geçersiz.');
        setUnauthorized(true);
      } else {
        setWaitingGuestName(false);
        setSessionData({ ...data, roles: [] });
      }
    } catch (err) {
      setErrorMsg('Bağlantı hatası.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="h-screen w-full bg-[#07080c] text-white flex flex-col items-center justify-center">
        <div className="w-9 h-9 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mb-3" />
        <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">Güvenli Kurumsal Oturum Doğrulanıyor...</span>
      </div>
    );
  }

  // 🔒 DIŞARIDAN ALAKASIZ BİRİ /embed ADRESİNE GİRERSE ÇIKACAK KİLİT EKRANI
  if (unauthorized || errorMsg) {
    return (
      <div className="h-screen w-full bg-[#07080c] text-white flex items-center justify-center p-6 select-none text-center">
        <div className="max-w-sm w-full bg-[#0d0f17] border border-white/10 rounded-3xl p-8 shadow-2xl space-y-4">
          <div className="w-16 h-16 rounded-2xl bg-red-500/15 border border-red-500/30 text-red-400 flex items-center justify-center mx-auto">
            <Lock size={30} />
          </div>
          <h2 className="text-lg font-black uppercase tracking-tight">Yetkisiz Erişim</h2>
          <p className="text-xs text-gray-400 leading-relaxed">
            {errorMsg || 'Bu iletişim modülü doğrudan tarayıcı üzerinden açılamaz. Yalnızca OMNIO veya yetkili kurumsal uygulamalar içerisinden şifreli oturum ile başlatılabilir.'}
          </p>
        </div>
      </div>
    );
  }

  // DIŞ MİSAFİR İSİM SORMA EKRANI
  if (waitingGuestName) {
    return (
      <div className="h-screen w-full bg-[#07080c] text-white flex items-center justify-center p-4">
        <form onSubmit={handleGuestJoin} className="w-full max-w-sm bg-[#0d0f17] border border-white/10 rounded-3xl p-7 space-y-4">
          <div className="w-12 h-12 rounded-2xl bg-indigo-500/20 text-indigo-400 flex items-center justify-center mx-auto">
            <ShieldCheck size={26} />
          </div>
          <h2 className="text-base font-black text-center">Kurumsal Misafir Bağlantısı</h2>
          <input
            type="text"
            required
            placeholder="Adınız / Firma Ünvanınız"
            value={guestName}
            onChange={e => setGuestName(e.target.value)}
            className="w-full bg-black/50 border border-white/10 rounded-xl px-3.5 py-2.5 text-xs text-white outline-none"
          />
          <button type="submit" className="w-full py-3 rounded-xl bg-indigo-600 text-xs font-black uppercase">
            Görüşmeye Katıl
          </button>
        </form>
      </div>
    );
  }

  // 🎨 ŞABLON YÖNLENDİRİCİ: OMNIO veya Şablon A ise OmnioTemplate açılır
  // (İleride başka şirketler için XSirketiTemplate.tsx açtığında buraya 1 satır if eklemen yeterli!)
  if (sessionData) {
    return <OmnioTemplate sessionData={sessionData} />;
  }

  return null;
};

export default B2BEmbedPortal;